import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "urql";
import { ProductCardWithQuickAdd } from "./ProductCardWithQuickAdd";
import { Product } from "../types/ProductType";
import { PRODUCTS_BY_CATEGORY_QUERY } from "../graphql/queries/categoryQuery";

type CategoryProductsProps = {
  categoryId: string;
  categoryName: string;
  categorySlug?: string;
  page?: number;
  perPage?: number;
  showViewAll?: boolean;
  gridCols?: 1 | 2 | 3 | 4;
  onAddToCart?: (success: boolean, message: string) => void;
};

export const CategoryProducts: React.FC<CategoryProductsProps> = ({
  categoryId,
  categoryName,
  categorySlug,
  page = 1,
  perPage = 8,
  showViewAll = true,
  gridCols = 4,
  onAddToCart,
}) => {
  const [{ data: productsData, fetching: productsFetching }] = useQuery({
    query: PRODUCTS_BY_CATEGORY_QUERY,
    variables: {
      category_id: categoryId,
      page,
      first: perPage,
    },
    pause: !categoryId,
    requestPolicy: "network-only",
  });

  const products = productsData?.productsByCategory?.data || [];

  if (products.length === 0 && !productsFetching) return null;

  const gridColsClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 sm:grid-cols-2",
    3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3",
    4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4",
  }[gridCols];

  return (
    <section className="mb-12">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">{categoryName}</h2>
          {showViewAll && categorySlug && (
            <Link
              to={`/category/${categorySlug}`}
              className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors duration-200 flex items-center gap-1"
            >
              Xem tất cả
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </Link>
          )}
        </div>

        {productsFetching ? (
          <div className={`grid ${gridColsClass} gap-6`}>
            {[...Array(perPage)].map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="h-48 bg-gray-200 rounded-t-lg"></div>
                <div className="p-4 space-y-3">
                  <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-8 bg-gray-200 rounded"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={`grid ${gridColsClass} gap-6`}>
            {products.map((product: Product) => (
              <ProductCardWithQuickAdd
                key={product.id}
                product={product}
                onAddToCart={onAddToCart}
              />
            ))}
          </div>
        )}
        </div>
      </div>
    </section>
  );
};

